<template>
    <b-container v-if="!ready">
        <b-row class="vh-100 text-center" align-v="center">
            <b-col>
                <b-spinner variant="light" />
            </b-col>
        </b-row>
    </b-container>
    <TemplateEditor v-model="nodes" @keyup="editorKeyup" :saving="saving" v-else >
        <template #nav>
            <b-nav-item-dropdown text="Files">
            <b-dropdown-item @click="save(false)">Save All</b-dropdown-item>
            <b-dropdown-item @click="save(true)">Save and Build</b-dropdown-item>
            </b-nav-item-dropdown>
        </template>
    </TemplateEditor>
</template>


<script>
const TemplateEditor = () => import(/* webpackChunkName: "template-editor" */ '../../components/editor/TemplateEditor')

export default {
    name: 'EditorV5',
    components: {
        TemplateEditor
    },

    props: {
        settings: {
            type: Object
        },

        app: {
            type: Object
        }
    },

    data: function() {
        return {
            appNodes: null,
            saving: false,
            ready: false,
            nodes: null
        }
    },
    computed: {
        scopeId() {
            return this.app.uuid.replace(/[0-9]/g, '').replaceAll('-', '');
        },

        version() {
            return this.$route.query.v
        }
    },
    methods: {
        save: _.debounce(async function(build) {
            this.saving = true;
            try {
                const nodes = JSON.parse(this.nodes[0].toString()).children

                await this.$http.put(`/v3/system/gateway/app-service/app/${this.app.uuid}/nodes`, {nodes, version: this.version, build})
            } catch (err) {
                this.$debugLog(err)
                if (err.response) {
                    this.$bvToast.toast(err.response.data.message, {
                        title: this.$t('error'),
                        variant: 'danger'
                    })

                    return
                }

                this.$bvToast.toast("Failed to save the app.", {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            } finally {
                this.saving = false
            }
        }, 500),

        editorKeyup(e) {
            if (e.ctrlKey && e.code == 'KeyS') {
                this.save(false)
            }
        }
    },

    async mounted() {
        try {
            let { nodes: appNodes = [] } = this.settings || {}

            let index = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                index = appNodes.find(n => {
                    return n.name.toLowerCase() == "index.vue" && n.ext == "vue"
                })
            }

            let routes = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                routes = appNodes.find(n => {
                    return n.name.toLowerCase() == "routes.js" && n.ext == "js"
                })
            }

            let config = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                config = appNodes.find(n => {
                    return n.name.toLowerCase() == "config.js" && n.ext == "js"
                })
            }

            let translation = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                translation = appNodes.find(n => {
                    return n.name.toLowerCase() == "i18n.js" && n.ext == "js"
                })
            }

            let store = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                store = appNodes.find(n => {
                    return n.name.toLowerCase() == "store.js" && n.ext == "js"
                })
            }

            let views = undefined;
            if (appNodes) {
                // check does templates have index.vue file if not add it programatically
                views = appNodes.find(n => {
                    return n.name.toLowerCase() == "views.json" && n.ext == "json"
                })
            }

            if (!index) {
                let indexNode = {
                    name: 'index.vue',
                    id: 3,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    ext: 'vue',
                    path: 'index.vue',
                    children: null,
                    editorOptions: {
                        mode: 'text/x-vue'
                    },
                    content: `<template>
    <div> Hello </div>
</template>
`,
                }

                appNodes.push(indexNode)
            }

            if (!routes) {
                let routesNode = {
                    name: 'routes.js',
                    id: 4,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    ext: 'js',
                    children: null,
                    path: 'routes.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    },
                    content: `export default []
`,
                }

                appNodes.push(routesNode)
            }

            if (!config) {
                let configNode = {
                    name: 'config.js',
                    id: 5,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `export default {
    env: {
        live: {},
        dev: {}
    }
}
`,
                    ext: 'js',
                    children: null,
                    path: 'config.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                appNodes.push(configNode)
            }

            if (!translation) {
                let configNode = {
                    name: 'i18n.js',
                    id: 6,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `
export default {
  locale: 'en',
  fallbackLocale: 'en',
  messages: {}
}
`,
                    ext: 'js',
                    children: null,
                    path: 'i18n.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                appNodes.push(configNode)
            }

            if (!store) {
                let configNode = {
                    name: 'store.js',
                    id: 7,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `
export default {
    // store modules
}
`,
                    ext: 'js',
                    children: null,
                    path: 'store.js',
                    editorOptions: {
                        mode: 'application/javascript'
                    }
                }

                appNodes.push(configNode)
            }

            if (!views) {
                let configNode = {
                    name: 'views.json',
                    id: 9,
                    pid: 1,
                    isLeaf: true,
                    dragDisabled: false,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    delNodeDisabled: true,
                    editNodeDisabled: true,
                    content: `{
    "appSettings": null,
    "exportedViews": []
}
`,
                    ext: 'json',
                    children: null,
                    path: 'views.json',
                    editorOptions: {
                        mode: 'application/json'
                    }
                }

                appNodes.push(configNode)
            }

            this.nodes = [
                {
                    name: this.app.name,
                    id: 1,
                    pid: 0,
                    root: true,
                    dragDisabled: false,
                    editNodeDisabled: true,
                    delNodeDisabled: true,
                    addTreeNodeDisabled: false,
                    addLeafNodeDisabled: false,
                    children: appNodes
                }
            ]

            this.ready = true
       }  catch(err) {
            this.$debugLog(err)
            this.$mainDialog.msgBoxOk("Failed to fetch component settings.")
        }

        // ── Kortex AI tools ──────────────────────────────────────
        if (!window.kortex) return

        window.kortex.showHelper()
        window.kortex.registerContext(
            'App Editor',
            'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
            'The user is editing a Protokol application in the visual code editor. The app consists of multiple files: ' +
            'index.vue (main template/routes), routes.js (route definitions), config.js (configuration), store.js (Vuex store), ' +
            'i18n.js (translations), and views.json (view definitions). ' +
            'You can read and modify any file, save all changes, and trigger a build. ' +
            'Use list_app_files to see the file tree, get_file_content to read a file, set_file_content to modify it, and save_app to persist.',
            'fa-file-code'
        )

        var self = this

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'list_app_files',
                    description: 'Lists all files in the app with their names, extensions, and content sizes.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var root = self.nodes && self.nodes[0]
                if (!root || !root.children) return { files: [], error: 'App not loaded' }
                var files = root.children.map(function (f) {
                    return {
                        name: f.name,
                        ext: f.ext || '',
                        size: f.content ? f.content.length : 0,
                        is_leaf: f.isLeaf || false
                    }
                })
                return { app_name: self.app ? self.app.name : '', files: files, count: files.length }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'get_file_content',
                    description: 'Returns the full content of a specific file in the app by filename (e.g. "index.vue", "routes.js").',
                    parameters: {
                        type: 'object',
                        required: ['filename'],
                        properties: {
                            filename: { type: 'string', description: 'The exact filename including extension (e.g. "index.vue", "config.js"). Case-sensitive.' }
                        }
                    }
                }
            },
            function (args) {
                var filename = args.filename
                var root = self.nodes && self.nodes[0]
                if (!root || !root.children) return { error: 'App files not loaded' }
                var file = root.children.find(function (f) { return f.name === filename })
                if (!file) {
                    var available = root.children.map(function (f) { return f.name }).join(', ')
                    return { error: 'File "' + filename + '" not found. Available files: ' + available }
                }
                return {
                    filename: file.name,
                    ext: file.ext || '',
                    content: file.content || '',
                    editor_mode: file.editorOptions ? file.editorOptions.mode : null
                }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'set_file_content',
                    description: 'Replaces the content of a specific file. The user must save to persist the change.',
                    parameters: {
                        type: 'object',
                        required: ['filename', 'content'],
                        properties: {
                            filename: { type: 'string', description: 'The exact filename including extension.' },
                            content: { type: 'string', description: 'The new file content.' }
                        }
                    }
                }
            },
            function (args) {
                var filename = args.filename
                var content = args.content
                var root = self.nodes && self.nodes[0]
                if (!root || !root.children) return { error: 'App files not loaded' }
                var file = root.children.find(function (f) { return f.name === filename })
                if (!file) {
                    var available = root.children.map(function (f) { return f.name }).join(', ')
                    return { error: 'File "' + filename + '" not found. Available: ' + available }
                }
                file.content = content
                return { filename: filename, length: content.length, saved: false, note: 'File content updated. User must save to persist.' }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'save_app',
                    description: 'Saves all files in the app. Set build=true to also trigger a build after saving.',
                    parameters: {
                        type: 'object',
                        properties: {
                            build: { type: 'boolean', description: 'If true, saves AND builds the app. Default is false (save only).' }
                        }
                    }
                }
            },
            function (args) {
                var build = args.build === true
                self.save(build)
                return { saved: true, build: build, note: build ? 'Save and build triggered.' : 'Save triggered.' }
            }
        )
    },

    beforeDestroy() {
        if (!window.kortex) return
        window.kortex.unregisterContext()
        window.kortex.hideHelper()
        window.kortex.unregisterTool('list_app_files')
        window.kortex.unregisterTool('get_file_content')
        window.kortex.unregisterTool('set_file_content')
        window.kortex.unregisterTool('save_app')
    }
}
</script>